import React, { useMemo, useEffect } from "react"; // useState entfernt
import { Accept, useDropzone } from "react-dropzone";
import { Grid2, IconButton, Typography } from "@mui/material";
import { Article, CloudUpload, Delete } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { convertToFileObject } from "./functions/convertToFileObject.function";
import { FileObject } from "./typings/FileObject.type";
import { handleFileRejections } from "./functions/handleFileRejections.function";

type DropzoneProps = {
  ["data-testid"]?: string;
  files?: FileObject[];
  dropzoneDesc: string;
  acceptedFileTypes: Accept;
  maxFiles: number;
  maxFileSize?: number;
  containerHeight?: string;
  thumbWidth?: number | string;
  onAdd: ((newFiles: FileObject[]) => void) | undefined;
  onDelete: ((deletedFileObject: FileObject, index: number) => void) | undefined;
};

const Dropzone: React.FC<DropzoneProps> = ({
  ["data-testid"]: dataTestId = "dropzone-input",
  files = [], // Wir nutzen direkt die Prop, keinen lokalen State mehr
  dropzoneDesc,
  acceptedFileTypes,
  maxFiles,
  maxFileSize = 5242880, // 5MB in bytes for documents, 2MB for images
  containerHeight = "200px",
  thumbWidth = 100,
  onAdd,
  onDelete
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  // ENTFERNT: const [files, setFiles] = useState...
  // ENTFERNT: useEffect(() => setFiles(externalFiles)... -> Das war der Auslöser des Fehlers

  const { getRootProps, getInputProps, fileRejections, isFocused, isDragAccept, isDragReject } =
    useDropzone({
      accept: { ...acceptedFileTypes },
      maxFiles: maxFiles,
      validator: file => {
        /* istanbul ignore next */
        if (file.size > maxFileSize) {
          return { code: "file-too-large", message: "too-large" };
        }
        return null;
      },
      onDropAccepted: async acceptedFiles => {
        const fileObjects = await Promise.all(acceptedFiles.map(file => convertToFileObject(file)));

        // Wir updaten nicht mehr lokal, sondern sagen nur dem Eltern-Element Bescheid
        onAdd?.(fileObjects);
      }
    });

  const baseStyle: React.CSSProperties = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    gap: "20px",
    borderWidth: 3,
    borderRadius: 2,
    borderColor: "#bdbdbd",
    borderStyle: "dashed",
    outline: "none",
    transition: "border .24s ease-in-out",
    cursor: "pointer",
    height: containerHeight
  };
  const focusedStyle: React.CSSProperties = { borderColor: "#2196f3" };
  const acceptStyle: React.CSSProperties = { borderColor: "#00e676" };
  const rejectStyle: React.CSSProperties = { borderColor: "#ff1744" };

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {})
    }),
    [isFocused, isDragAccept, isDragReject]
  );

  const thumbsContainer: React.CSSProperties = {
    display: "flex",
    flexWrap: "wrap",
    marginTop: 16
  };

  const img: React.CSSProperties = {
    display: "block",
    width: "auto",
    height: "100%"
  };

  const thumb: React.CSSProperties = {
    position: "relative",
    display: "inline-flex",
    marginBottom: 8,
    marginRight: 8,
    width: thumbWidth,
    height: 100,
    padding: 4,
    boxSizing: "border-box",
    justifyContent: "center"
  };

  const thumbInner: React.CSSProperties = {
    display: "flex",
    minWidth: 0,
    overflow: "hidden"
  };

  const handleRemove = (fileObj: FileObject, index: number) => {
    // Wir löschen nicht lokal, sondern rufen nur onDelete auf.
    // Die Eltern-Komponente muss die Datei aus der Liste entfernen und die neue Liste zurückgeben.
    onDelete?.(fileObj, index);
  };

  const thumbs = (() => {
    if (!files.length) return;

    return files.map((fileObj, index) => {
      // WICHTIG: files ist jetzt direkt die Prop
      const { file } = fileObj;
      const isImage = file.type.includes("image");

      return (
        <div style={thumb} key={`${file.name}-${index}`} data-testid={`file-thumb-${index}`}>
          <IconButton
            aria-label={`Remove ${file.name}`}
            data-testid={`remove-file-btn-${file.name}`}
            // Hier übergeben wir direkt das Objekt und den Index
            onClick={() => handleRemove(fileObj, index)}
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              backgroundColor: "rgba(0,0,0,0.6)",
              color: "#fff",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.8)"
              }
            }}
            size="small"
          >
            <Delete fontSize="small" />
          </IconButton>

          <div style={thumbInner} data-testid={`file-thumb-${index}-parent`}>
            {isImage ? (
              <img
                data-testid="image-file-item"
                src={file.preview}
                style={img}
                alt={file.name}
                // Cleanup Logik
                onLoad={() => {
                  // Optional: Prüfen ob revoke nötig ist, oft handhaben Parent-Comps das
                  // URL.revokeObjectURL(file.preview);
                }}
              />
            ) : (
              <Grid2 data-testid="non-image-file-item">
                <Article />
                <Typography>{file.name}</Typography>
              </Grid2>
            )}
          </div>
        </div>
      );
    });
  })();

  useEffect(() => {
    // Cleanup Effect, wenn files sich ändern (unmounted previews)
    return () => {
      files.forEach(file => {
        if (file.file.preview) URL.revokeObjectURL(file.file.preview);
      });
    };
  }, [files]);

  useEffect(() => {
    handleFileRejections({ fileRejections, maxFiles, enqueueSnackbar, t });
  }, [fileRejections]);

  return (
    <section data-testid="dropzone-container" className="container" style={{ width: "100%" }}>
      <div {...getRootProps({ style })}>
        <input {...getInputProps()} data-testid={dataTestId} />
        <Typography variant={"h6"}>{dropzoneDesc}</Typography>
        <CloudUpload fontSize={"large"} />
      </div>

      <aside style={thumbsContainer}>{thumbs}</aside>
    </section>
  );
};

export default Dropzone;
